import { FC, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppStateContext } from "../context/state-context-provider";
import {
  ApplyUserLocation,
  BuildLocationPath,
  BuildUserLocationPath,
  ReadUserLocation,
} from "../../helper/ui/methods";
import { UserLocationInterface } from "../interfaces/user/user-location-interface";

// Keeps the address and the open project, folder and report in step. When the
// user moves inside the app, the address follows with one history entry per
// move. When the address moves on its own (Back, Forward, a typed or copied
// link), the app follows it.
export const LocationHandler: FC = () => {
  const [state, dispatch] = useAppStateContext();
  const location = useLocation();
  const navigate = useNavigate();
  const locationKeyRef = useRef<string | null>(null);
  const followingAddressRef = useRef(false);
  const pendingLocationRef = useRef<UserLocationInterface | null>(null);

  const statePath = state.user ? BuildLocationPath(state.user) : null;

  useEffect(() => {
    const firstRun = locationKeyRef.current === null;
    const locationChanged = locationKeyRef.current !== location.key;
    locationKeyRef.current = location.key;

    const userLocation = ReadUserLocation(location.pathname, location.search);

    if (!state.user || !statePath) {
      // A refresh starts signed out while the session is checked, and the
      // sign-in redirect drops the address. Remember where it pointed.
      if (firstRun && userLocation?.projectID) {
        pendingLocationRef.current = userLocation;
      }
      return;
    }

    if (!userLocation) {
      followingAddressRef.current = false;
      return;
    }

    const addressPath = BuildUserLocationPath(userLocation);

    if (followingAddressRef.current) {
      // The state has caught up with the address it was asked to follow. It
      // can differ only by a folder or report that no longer exists.
      followingAddressRef.current = false;
      if (addressPath !== statePath) {
        navigate(statePath, { replace: true });
      }
      return;
    }

    const pendingLocation = pendingLocationRef.current;
    if (pendingLocation) {
      pendingLocationRef.current = null;
      if (ApplyUserLocation(pendingLocation, state, dispatch)) {
        followingAddressRef.current = true;
        return;
      }
    }

    // Signing in (or a page bouncing through sign-in) reopens whatever the
    // session last had open, so the state leads there. Replacing the entry
    // also drops the marker, so Back onto it later lets the address lead.
    if ((location.state as { fromSignIn?: boolean } | null)?.fromSignIn) {
      navigate(statePath, { replace: true });
      return;
    }

    if (addressPath === statePath) {
      return;
    }

    if (
      locationChanged &&
      addressPath !== null &&
      ApplyUserLocation(userLocation, state, dispatch)
    ) {
      followingAddressRef.current = true;
      return;
    }

    navigate(statePath, { replace: locationChanged });
  }, [location.key, statePath]);

  return <></>;
};
